// GlobalDrawer — wraps DS `Drawer` for shell-level slide-in panels (US-052).
//
// DS-GAP-DESIGN (US-052):
//   Same imperative-bus pattern as `GlobalConfirmModal`. A feature page calls
//   `openDrawer({ title, body })` and the shell-mounted root renders the DS
//   `Drawer`. Side-mounted panels that need richer composition (`<DrawerBody>`
//   + custom footer) can pass a `render` function instead of a static body.
//
//   `closeDrawer()` and ESC / outside-click both flush the active drawer.

import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';

import { useT } from '@barel/i18n';
import {
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  type DrawerWidth,
} from '@barel/ui';

export interface OpenDrawerOptions {
  /** Pre-localised title. Falls back to `drawer.defaultTitle`. */
  title?: ReactNode;
  /** Pre-localised description shown below the title. */
  description?: ReactNode;
  /** Body content. */
  body?: ReactNode;
  /** Optional footer (action row). */
  footer?: ReactNode;
  /** Width preset — sm / md / lg. Defaults `md` (440px). */
  width?: DrawerWidth;
  /** Side of the viewport. Defaults to `'right'`. */
  side?: 'right' | 'left';
}

interface QueueEntry extends OpenDrawerOptions {
  id: string;
}

type Listener = (entry: QueueEntry | null) => void;

interface DrawerBus {
  subscribe(fn: Listener): () => void;
  getSnapshot(): QueueEntry | null;
  open(opts: OpenDrawerOptions): string;
  close(id?: string): void;
}

function createBus(): DrawerBus {
  let current: QueueEntry | null = null;
  const listeners = new Set<Listener>();
  let counter = 0;

  function emit() {
    for (const fn of listeners) fn(current);
  }

  return {
    subscribe(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    getSnapshot() {
      return current;
    },
    open(opts) {
      counter += 1;
      const id = `drawer-${counter}-${Date.now().toString(36)}`;
      current = { ...opts, id };
      emit();
      return id;
    },
    close(id) {
      if (id && current?.id !== id) return;
      current = null;
      emit();
    },
  };
}

const bus: DrawerBus = createBus();

/** Imperative API — open the global drawer. Returns the drawer id for later `closeDrawer(id)`. */
export function openDrawer(opts: OpenDrawerOptions = {}): string {
  return bus.open(opts);
}

/** Imperative API — close the global drawer. */
export function closeDrawer(id?: string): void {
  bus.close(id);
}

/**
 * Global drawer mount. Mount once at the App root (alongside Toaster /
 * ConfirmModal). One drawer at a time; calling `openDrawer` while one is
 * open replaces it (matches the prototype's `LeadDetailPanel` behaviour).
 */
export function GlobalDrawer() {
  const t = useT('shell');
  const entry = useSyncExternalStore(bus.subscribe, bus.getSnapshot, bus.getSnapshot);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(entry !== null);
  }, [entry]);

  if (!entry) return null;

  const handleOpenChange = (next: boolean): void => {
    if (!next) {
      bus.close();
    }
    setOpen(next);
  };

  return (
    <DrawerRoot open={open} onOpenChange={handleOpenChange}>
      <DrawerContent
        width={entry.width ?? 'md'}
        side={entry.side ?? 'right'}
        data-testid="global-drawer"
      >
        <DrawerHeader
          title={entry.title ?? t('drawer.defaultTitle')}
          description={entry.description}
          onClose={() => handleOpenChange(false)}
        />
        <DrawerBody>{entry.body}</DrawerBody>
        {entry.footer ? <DrawerFooter>{entry.footer}</DrawerFooter> : null}
      </DrawerContent>
    </DrawerRoot>
  );
}
